import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Calendar as CalendarIcon,
  Users,
  DollarSign,
  BarChart3,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';
import { format } from 'date-fns';

const MovementAnalytics = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [commitments, setCommitments] = useState([]);
  const [filteredCommitments, setFilteredCommitments] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedCommitment, setSelectedCommitment] = useState(null);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Default to current month
  useEffect(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setStartDate(firstDay);
    setEndDate(lastDay);
  }, []);

  // Fetch data when dates change
  useEffect(() => {
    if (startDate && endDate) {
      fetchAnalytics();
    }
  }, [startDate, endDate]);

  // Filter commitments by tab
  useEffect(() => {
    if (activeTab === 'pending') {
      setFilteredCommitments(
        commitments.filter(c => c.commitment_status === 'pending' || c.commitment_status === 'partial')
      );
    } else {
      setFilteredCommitments(
        commitments.filter(c => c.commitment_status === 'fulfilled')
      );
    }
  }, [activeTab, commitments]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/movement-analytics', {
        params: {
          start_date: format(startDate, 'yyyy-MM-dd'),
          end_date: format(endDate, 'yyyy-MM-dd')
        }
      });

      setMetrics(response.data.metrics);
      setCommitments(response.data.commitments);
    } catch (error) {
      console.error('Failed to fetch movement analytics:', error);
      toast.error('Failed to load movement data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const openTimeline = (commitment) => {
    setSelectedCommitment(commitment);
    setTimelineOpen(true);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Movement Analytics</h1>
        <p className="text-gray-600 mt-1">Track and analyze commitment movements</p>
      </div>

      {/* Date Range Picker */}
      <div className="flex gap-4 mb-6">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-64">
              <CalendarIcon className="mr-2 h-4 w-4" />
              {startDate ? format(startDate, 'PPP') : 'Start Date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              selected={startDate}
              onSelect={setStartDate}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-64">
              <CalendarIcon className="mr-2 h-4 w-4" />
              {endDate ? format(endDate, 'PPP') : 'End Date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              selected={endDate}
              onSelect={setEndDate}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        <Button onClick={fetchAnalytics} disabled={loading}>
          Refresh
        </Button>
      </div>

      {/* Metrics Cards */}
      {metrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Moved</CardTitle>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.total_moved}</div>
              <p className="text-xs text-muted-foreground">Commitments</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Revenue Pushed</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(metrics.total_revenue_pushed)}</div>
              <p className="text-xs text-muted-foreground">To future dates</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.moved_conversion_rate}%</div>
              <p className="text-xs text-muted-foreground">
                Moved vs {metrics.non_moved_conversion_rate}% non-moved
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Top Mover</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {metrics.top_tls_by_movement[0]?.name?.split(' ')[0] || 'N/A'}
              </div>
              <p className="text-xs text-muted-foreground">
                {metrics.top_tls_by_movement[0]?.count || 0} movements
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending">Moved - Awaiting Closure</TabsTrigger>
          <TabsTrigger value="fulfilled">Moved - Successfully Closed</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          <Card>
            <CardContent className="pt-6">
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : filteredCommitments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No {activeTab === 'pending' ? 'moved commitments awaiting closure' : 'moved commitments that were successfully closed'} found
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Date</th>
                        <th className="text-left p-2">Gym</th>
                        <th className="text-left p-2">TL</th>
                        <th className="text-left p-2">Source</th>
                        <th className="text-right p-2">Expected</th>
                        <th className="text-right p-2">Revenue</th>
                        <th className="text-center p-2">Moves</th>
                        <th className="text-center p-2">Status</th>
                        <th className="text-center p-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCommitments.map((commitment) => (
                        <tr key={commitment.id} className="border-b hover:bg-gray-50">
                          <td className="p-2 text-sm">{commitment.current_date || commitment.entry_date}</td>
                          <td className="p-2 text-sm">{commitment.gym_name}</td>
                          <td className="p-2 text-sm">{commitment.filled_by_name}</td>
                          <td className="p-2 text-sm">{commitment.source}</td>
                          <td className="p-2 text-sm text-right">{commitment.closure_expected}</td>
                          <td className="p-2 text-sm text-right font-medium">
                            {formatCurrency(commitment.revenue_expected)}
                          </td>
                          <td className="p-2 text-center">
                            <Badge variant={commitment.times_moved >= 2 ? "destructive" : "secondary"}>
                              {commitment.times_moved}/3
                            </Badge>
                          </td>
                          <td className="p-2 text-center">
                            <Badge
                              variant={
                                commitment.commitment_status === 'fulfilled'
                                  ? 'success'
                                  : commitment.commitment_status === 'partial'
                                  ? 'warning'
                                  : 'default'
                              }
                            >
                              {commitment.commitment_status}
                            </Badge>
                          </td>
                          <td className="p-2 text-center">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openTimeline(commitment)}
                            >
                              <Clock className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Timeline Modal */}
      {selectedCommitment && (
        <Dialog open={timelineOpen} onOpenChange={setTimelineOpen}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Movement Timeline - {selectedCommitment.gym_name}</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <div className="mb-4 text-sm text-gray-600">
                <div><strong>TL:</strong> {selectedCommitment.filled_by_name}</div>
                <div><strong>Source:</strong> {selectedCommitment.source}</div>
                <div><strong>Revenue:</strong> {formatCurrency(selectedCommitment.revenue_expected)}</div>
              </div>

              <div className="space-y-4">
                {/* Creation Event */}
                <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg">
                  <div className="flex-shrink-0 w-24 text-sm font-medium text-blue-900">
                    {selectedCommitment.original_date || selectedCommitment.entry_date}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">Created</div>
                    <div className="text-sm text-gray-600">
                      by {selectedCommitment.filled_by_name}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {new Date(selectedCommitment.created_at).toLocaleString('en-IN')}
                    </div>
                  </div>
                </div>

                {/* Movement Events */}
                {selectedCommitment.movements?.map((movement, idx) => (
                  <div key={idx} className="relative">
                    <div className="absolute left-12 top-0 bottom-0 w-0.5 bg-gray-300" />
                    <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0">
                        <ArrowRight className="w-5 h-5 text-gray-400" />
                      </div>
                      <div className="flex-shrink-0 w-24 text-sm font-medium text-gray-900">
                        {movement.to_date}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">
                          Moved #{idx + 1}/3
                        </div>
                        <div className="text-sm text-gray-600">
                          by {movement.moved_by_name}
                        </div>
                        <div className="text-sm mt-2 p-2 bg-white rounded border">
                          <strong>Reason:</strong> {movement.note}
                        </div>
                        <div className="text-sm text-blue-600 mt-1">
                          <strong>Revenue Impact:</strong> {formatCurrency(movement.revenue_impact)}
                          {' '}from {movement.from_date} → {movement.to_date}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {new Date(movement.moved_at).toLocaleString('en-IN')}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default MovementAnalytics;
